import { z } from 'zod';

import { articleStatusSchema, type ArticleStatus } from './article.js';
import { type Role } from './auth.js';
import { idSchema, isoDateTimeSchema } from './common.js';

/**
 * Matriz de permissões do workflow editorial.
 *
 * Cada entrada define uma transição permitida para um papel, com flag
 * indicando se o comentário no review_log é obrigatório (devoluções e
 * arquivamento exigem comentário; aprovações e avanços não).
 *
 * Hierarquia: admin > publisher > revisor > editor. Admin tem acesso a
 * todas as transições atribuídas a papéis abaixo via union — para evitar
 * duplicação, a função `getAllowedTransitions(role)` faz a união.
 */
export interface TransitionRule {
  from: ArticleStatus;
  to: ArticleStatus;
  comentarioObrigatorio?: boolean;
}

const EDITOR_RULES: TransitionRule[] = [
  { from: 'rascunho', to: 'em_revisao' },
  { from: 'rascunho', to: 'arquivado', comentarioObrigatorio: true },
];

const REVISOR_RULES: TransitionRule[] = [
  { from: 'em_revisao', to: 'aprovado' },
  { from: 'em_revisao', to: 'rascunho', comentarioObrigatorio: true },
  { from: 'em_revisao', to: 'arquivado', comentarioObrigatorio: true },
];

const PUBLISHER_RULES: TransitionRule[] = [
  { from: 'aprovado', to: 'publicado' },
  { from: 'aprovado', to: 'em_revisao', comentarioObrigatorio: true },
  { from: 'publicado', to: 'arquivado', comentarioObrigatorio: true },
  { from: 'publicado', to: 'rascunho', comentarioObrigatorio: true },
];

/**
 * Regras adicionais exclusivas do admin — atalhos para correções emergenciais.
 * Editor/revisor/publisher também herdam tudo de papéis abaixo da hierarquia.
 */
const ADMIN_EXTRA_RULES: TransitionRule[] = [
  { from: 'rascunho', to: 'publicado' },
  { from: 'em_revisao', to: 'publicado', comentarioObrigatorio: true },
  { from: 'aprovado', to: 'arquivado', comentarioObrigatorio: true },
  { from: 'aprovado', to: 'rascunho', comentarioObrigatorio: true },
];

/**
 * Une as regras herdadas pela hierarquia. Editor só vê EDITOR_RULES.
 * Revisor vê EDITOR + REVISOR. Publisher vê EDITOR + REVISOR + PUBLISHER.
 * Admin vê tudo (+ extras).
 *
 * Deduplica pares from→to (a regra mais permissiva ganha — comentário
 * opcional sobrescreve obrigatório).
 */
export function getAllowedTransitions(role: Role): TransitionRule[] {
  const layered: TransitionRule[][] = [];
  if (role === 'editor' || role === 'revisor' || role === 'publisher' || role === 'admin') {
    layered.push(EDITOR_RULES);
  }
  if (role === 'revisor' || role === 'publisher' || role === 'admin') {
    layered.push(REVISOR_RULES);
  }
  if (role === 'publisher' || role === 'admin') {
    layered.push(PUBLISHER_RULES);
  }
  if (role === 'admin') {
    layered.push(ADMIN_EXTRA_RULES);
  }

  const map = new Map<string, TransitionRule>();
  for (const rules of layered) {
    for (const r of rules) {
      const key = `${r.from}→${r.to}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, r);
      } else if (existing.comentarioObrigatorio && !r.comentarioObrigatorio) {
        // Regra mais permissiva ganha
        map.set(key, r);
      }
    }
  }
  return [...map.values()];
}

/**
 * Encontra a regra exata para uma transição. Retorna `null` se o papel
 * não pode fazer essa transição.
 */
export function findTransitionRule(
  role: Role,
  from: ArticleStatus,
  to: ArticleStatus,
): TransitionRule | null {
  return getAllowedTransitions(role).find((r) => r.from === from && r.to === to) ?? null;
}

/**
 * Input do POST /articles/:id/transition.
 *
 * Quando `para === 'publicado'` e `agendadoPara` é fornecido com data
 * futura (> 5s no futuro), o backend **não publica imediatamente**:
 * mantém o status atual + seta `agendadoPara` + enfileira job BullMQ
 * que vai publicar quando chegar a hora.
 */
export const transitionInputSchema = z.object({
  para: articleStatusSchema,
  comentario: z.string().max(2000).optional().or(z.literal('')),
  /** Mensagem opcional gravada na versão criada antes da transição. */
  mensagem: z.string().max(500).optional().or(z.literal('')),
  /** Quando para=publicado e este campo é futuro, agenda em vez de publicar. */
  agendadoPara: z.string().datetime({ offset: true }).optional().or(z.literal('')),
});
export type TransitionInput = z.infer<typeof transitionInputSchema>;

/**
 * Versão snapshot do artigo. `snapshot` é jsonb com os campos editáveis
 * (não inclui id nem timestamps). `autorNome` é populado server-side
 * mesmo se o user for deletado depois.
 */
export const articleVersionSchema = z.object({
  id: idSchema,
  articleId: idSchema,
  snapshot: z.record(z.unknown()),
  autorId: idSchema.nullable(),
  autorNome: z.string(),
  mensagem: z.string().nullable(),
  criadoEm: isoDateTimeSchema,
});
export type ArticleVersion = z.infer<typeof articleVersionSchema>;

/** Entrada do log de revisão (uma transição). */
export const articleReviewLogEntrySchema = z.object({
  id: idSchema,
  articleId: idSchema,
  deStatus: articleStatusSchema.nullable(),
  paraStatus: articleStatusSchema,
  autorId: idSchema.nullable(),
  autorNome: z.string(),
  comentario: z.string().nullable(),
  criadoEm: isoDateTimeSchema,
});
export type ArticleReviewLogEntry = z.infer<typeof articleReviewLogEntrySchema>;
